import React from "react";
import ModalActionButton from "../../elements/ModalActionButton";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { authHeader, baseUrl } from "../../../utils";
import { ERROR, SET_SUCCESS } from "../../../store/types";
import TextField from "../../elements/form/TextField";
import Loader from "../../elements/Loader";
import AutocompleteSelect from "../../elements/form/AutocompleteSelect";

const AddEditBankDetails = ({ staff, close, reload }) => {
  const [processing, setProcessing] = React.useState(false);
  const [accountPayload, setAccountPayload] = useState({});
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [validationErrors, setValidationErrors] = useState({});
  const [banks, setBanks] = useState([]);
  const [validatingAccount, setValidatingAccount] = useState(false);
  const [validatedAccount, setValidatedAccount] = useState(null);

  useEffect(() => {
    const fetchBanks = async () => {
      const headers = authHeader();

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/utilities/banks`,
          { headers },
        );
        setBanks(response.data.data);
        setLoading(false);
      } catch (error) {
        console.log("error validating account: ", error);
        dispatch({
          type: ERROR,
          error,
        });
        setLoading(false);
      }
    };

    fetchBanks();
    return () => {};
  }, [dispatch]);

  const validateForm = () => {
    let errors = {};

    if (!accountPayload.accountNumber || accountPayload.accountNumber === "") {
      errors.accountNumber = true;
    }

    if (!accountPayload.bank || accountPayload.bank === "") {
      errors.bank = true;
    }

    setValidationErrors(errors);

    return errors;
  };

  const [accountValidationError, setAccountValidationError] = useState(false);

  const validateBankAccount = useCallback(async () => {
    const headers = authHeader();

    try {
      const requestPayload = {
        accountNumber: accountPayload.accountNumber,
        bankCode: accountPayload.bankCode,
      };

      setValidatingAccount(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/utilities/validate-bank-account`,
        requestPayload,
        { headers },
      );
      if (response.data.success) {
        setValidatedAccount(response.data.data);
        setValidatingAccount(false);
        setAccountValidationError(false);
      }
    } catch (error) {
      console.log("error validating account: ", error);
      dispatch({
        type: ERROR,
        error,
      });
      setValidatingAccount(false);
      setAccountValidationError(true);
    }
  }, [accountPayload.accountNumber, accountPayload.bankCode, dispatch]);

  useEffect(() => {
    if (
      accountPayload.accountNumber?.length === 10 &&
      accountPayload?.bankCode &&
      accountPayload.bankCode !== ""
    ) {
      validateBankAccount();
    }
    return () => {};
  }, [
    accountPayload.accountNumber,
    accountPayload.bankCode,
    validateBankAccount,
  ]);

   const pushUpdate = async () => {
    if (Object.values(validateForm()).includes(true)) {
        return
    }
    const headers = { 'Content-Type': 'application/json', ...authHeader() }
    const payload = {bankDetails: {...accountPayload, accountName: validatedAccount?.accountName}}

    try {
      setProcessing(true) 
      await axios.patch(`${baseUrl}/user/profile/staff`, payload, { headers })
      setProcessing(false)
      dispatch({
          type: SET_SUCCESS,
          payload: "Bank information updated successfully"
      })
      reload()
      close()
    } catch (error) {
      console.error(error)
      dispatch({
          type: ERROR,
          error
      })
      setProcessing(false)    
    }
  }

  return (
    <div className="w-full">
      <div className="mt-2">
        <TextField
          inputLabel="Account number (NUBAN)"
          inputPlaceholder={`10-digit account number`}
          fieldId="account-number"
          inputType="text"
          requiredField={true}
          preloadValue={accountPayload.accountNumber || ""}
          hasError={validationErrors && validationErrors.accountNumber}
          returnFieldValue={(value) => {
            setAccountPayload({
              ...accountPayload,
              ...{ accountNumber: value },
            });
          }}
        />
      </div>
      <div className="mt-2">
        {loading ? (
          <Loader />
        ) : banks && banks.length > 0 ? (
          <AutocompleteSelect
            selectOptions={banks}
            inputLabel="Account Bank"
            placeholderText={`Select a bank`}
            titleField="name"
            displayImage={false}
            imageField=""
            requiredField={true}
            preSelected={accountPayload.bankCode || ""}
            preSelectedLabel="bankCode"
            fieldId="fee-select"
            position="bottom-[50px]"
            hasError={validationErrors && validationErrors.bank}
            // return id of accounts of the selected option
            returnFieldValue={(value) => {
              setAccountPayload({
                ...accountPayload,
                ...{
                  bankCode: value.code,
                  bank: value.name,
                },
              });
            }}
          />
        ) : (
          <div className="w-full bg-red-50 p-3 rounded text-xs text-gray-700 flex items-center">
            <p className="text-xs font-[550] text-red-600">
              Banks not available at the moment, please try again later
            </p>
          </div>
        )}
      </div>
      <div className="mt-2">
        {validatingAccount && <Loader />}
        {validatedAccount && (
          <div className="w-full mt-4 p-[10px] rounded-[8px] bg-vcm-light-purple bg-opacity-10">
            <label className="block text-xs lg:text-md cursor-text z-10 relative py-1 transition">
              Account name
            </label>
            <p className="capitalize font-[550]">
              {validatedAccount?.accountName?.toLowerCase()}
            </p>
          </div>
        )}
        {accountValidationError && !validatingAccount && (
          <div className="w-full bg-red-50 p-3 rounded text-xs text-gray-700 flex items-center">
            <p className="text-xs font-[550] text-red-600">
              Sorry there was an error validating your account, please check the
              details or please try again later
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-row-reverse gap-x-4">
        <ModalActionButton
          buttonLabel={`Update Bank Details`}
          buttonAction={() => {
            pushUpdate();
          }}
          processing={processing}
          disabled={!validatedAccount || validatingAccount}
        />

        <button
          type="button"
          className="inline-flex font-space-grotesk justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-xl transition duration-200 hover:bg-opacity-10 hover:bg-secondary focus:outline-none"
          onClick={close}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default AddEditBankDetails;
